import React from "react";
import { Button } from "@shadcn-in-nx/ui/button";
import { AlertTriangle, Trash2, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";

function DeleteConfirmationModal({
    open,
    onOpenChange,
    onConfirm,
    itemType,
    itemName,
    isDeleting,
    isAdmin,
}) {
    const { t } = useTranslation();
    const [confirmText, setConfirmText] = React.useState("");

    // Reset confirm text when modal opens
    React.useEffect(() => {
        if (open) {
            setConfirmText("");
        }
    }, [open]);

    const canDelete = confirmText.toLowerCase() === "proceed" && isAdmin;

    const handleConfirm = () => {
        if (canDelete) {
            onConfirm(confirmText);
        }
    };

    const handleOpenChange = (open) => {
        onOpenChange(open);
        if (!open) {
            setConfirmText("");
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-600">
                        <Trash2 className="w-5 h-5" />
                        {t("databases.deleteItem", { item: itemType })}
                    </DialogTitle>
                    <DialogDescription className="pt-2">
                        {isAdmin ? (
                            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                                <div className="text-red-800">
                                    <p className="font-medium">
                                        {t("databases.deleteWarning")}
                                    </p>
                                    <p className="text-sm mt-1">
                                        {t("databases.deleteDescription", {
                                            name: itemName,
                                            type: itemType,
                                        })}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                                <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                <div className="text-amber-800">
                                    <p className="font-medium">
                                        {t("databases.adminOnlyDelete")}
                                    </p>
                                    <p className="text-sm mt-1">
                                        {t("databases.contactAdminToDelete")}
                                    </p>
                                </div>
                            </div>
                        )}
                    </DialogDescription>
                </DialogHeader>

                {isAdmin && (
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="confirm-text">
                                {t("databases.typeToConfirm")}
                            </Label>
                            <Input
                                id="confirm-text"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder="proceed"
                                autoComplete="off"
                            />
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        disabled={isDeleting}
                    >
                        {isAdmin ? t("common.cancel") : t("common.close")}
                    </Button>
                    {isAdmin && (
                        <Button
                            variant="destructive"
                            onClick={handleConfirm}
                            disabled={!canDelete || isDeleting}
                        >
                            {isDeleting ? (
                                <div className="flex items-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    {t("common.deleting")}
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <Trash2 className="w-4 h-4" />
                                    {t("common.delete")}
                                </div>
                            )}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DeleteConfirmationModal;
